import { supabase } from './supabase';

export interface TeacherPortalClass {
  classId: string;
  className: string;
  levelCode: string;
  levelName: string;
  academicYear: string;
  joinCode: string;
  studentCount: number;
  openAssignments: number;
  averageAccuracy: number;
  createdAt: string;
}

export interface TeacherClassSummary {
  classId: string;
  className: string;
  levelCode: string;
  academicYear: string;
  joinCode: string;
  studentCount: number;
  openAssignments: number;
  completedAttempts: number;
  averageAccuracy: number;
  studentsNeedingSupport: number;
  lastActivityAt: string | null;
}

export interface TeacherClassStudent {
  childId: string;
  displayName: string;
  levelCode: string;
  enrolledAt: string;
  completedAttempts: number;
  quizAccuracyPercent: number;
  mathAccuracyPercent: number;
  wordAccuracyPercent: number;
  lastActivityAt: string | null;
}

export interface TeacherAssignableQuiz {
  quizId: string;
  title: string;
  subjectName: string;
  questionCount: number;
  quizScope: string;
}

export interface TeacherClassAssignment {
  assignmentId: string;
  quizId: string;
  quizTitle: string;
  status: string;
  startsAt: string | null;
  dueAt: string | null;
  attemptLimit: number;
  timeLimitSeconds: number | null;
  completedAttempts: number;
  studentCount: number;
}

function throwIfError(error: { message: string } | null) {
  if (error) throw new Error(error.message);
}

export async function listTeacherPortalClasses(): Promise<TeacherPortalClass[]> {
  const { data, error } = await supabase.rpc('list_teacher_portal_classes');
  throwIfError(error);
  return (data ?? []).map((row: Record<string, unknown>) => ({
    classId: String(row.class_id),
    className: String(row.class_name ?? 'Class'),
    levelCode: String(row.level_code ?? ''),
    levelName: String(row.level_name ?? row.level_code ?? ''),
    academicYear: String(row.academic_year ?? ''),
    joinCode: String(row.join_code ?? ''),
    studentCount: Number(row.student_count ?? 0),
    openAssignments: Number(row.open_assignments ?? 0),
    averageAccuracy: Number(row.average_accuracy ?? 0),
    createdAt: String(row.created_at ?? ''),
  }));
}

export async function createTeacherClass(name: string, levelCode: string, academicYear: string): Promise<string> {
  const { data, error } = await supabase.rpc('create_teacher_class', {
    p_name: name,
    p_level_code: levelCode,
    p_academic_year: academicYear,
  });
  throwIfError(error);
  if (!data) throw new Error('The class could not be created.');
  return String(data);
}

export async function loadTeacherClassSummary(classId: string): Promise<TeacherClassSummary> {
  const { data, error } = await supabase.rpc('get_teacher_class_summary', { p_class_id: classId });
  throwIfError(error);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('No class summary was returned.');
  return {
    classId: String(row.class_id), className: String(row.class_name ?? 'Class'),
    levelCode: String(row.level_code ?? ''), academicYear: String(row.academic_year ?? ''),
    joinCode: String(row.join_code ?? ''), studentCount: Number(row.student_count ?? 0),
    openAssignments: Number(row.open_assignments ?? 0), completedAttempts: Number(row.completed_attempts ?? 0),
    averageAccuracy: Number(row.average_accuracy ?? 0), studentsNeedingSupport: Number(row.students_needing_support ?? 0),
    lastActivityAt: (row.last_activity_at as string | null | undefined) ?? null,
  };
}

export async function listTeacherClassStudents(classId: string): Promise<TeacherClassStudent[]> {
  const { data, error } = await supabase.rpc('list_teacher_class_students', { p_class_id: classId });
  throwIfError(error);
  return (data ?? []).map((row: Record<string, unknown>) => ({
    childId: String(row.child_id), displayName: String(row.display_name ?? 'Learner'),
    levelCode: String(row.level_code ?? ''), enrolledAt: String(row.enrolled_at ?? ''),
    completedAttempts: Number(row.completed_attempts ?? 0), quizAccuracyPercent: Number(row.quiz_accuracy_percent ?? 0),
    mathAccuracyPercent: Number(row.math_accuracy_percent ?? 0), wordAccuracyPercent: Number(row.word_accuracy_percent ?? 0),
    lastActivityAt: (row.last_activity_at as string | null | undefined) ?? null,
  }));
}

export async function listTeacherAssignableQuizzes(classId: string): Promise<TeacherAssignableQuiz[]> {
  const { data, error } = await supabase.rpc('list_teacher_assignable_quizzes', { p_class_id: classId });
  throwIfError(error);
  return (data ?? []).map((row: Record<string, unknown>) => ({
    quizId: String(row.quiz_id), title: String(row.title ?? 'Quiz'),
    subjectName: String(row.subject_name ?? 'Mixed'), questionCount: Number(row.question_count ?? 0),
    quizScope: String(row.quiz_scope ?? ''),
  }));
}

export async function listTeacherClassAssignments(classId: string): Promise<TeacherClassAssignment[]> {
  const { data, error } = await supabase.rpc('list_teacher_class_assignments', { p_class_id: classId });
  throwIfError(error);
  return (data ?? []).map((row: Record<string, unknown>) => ({
    assignmentId: String(row.assignment_id), quizId: String(row.quiz_id), quizTitle: String(row.quiz_title ?? 'Quiz'),
    status: String(row.status ?? 'draft'), startsAt: (row.starts_at as string | null | undefined) ?? null,
    dueAt: (row.due_at as string | null | undefined) ?? null, attemptLimit: Number(row.attempt_limit ?? 1),
    timeLimitSeconds: row.time_limit_seconds == null ? null : Number(row.time_limit_seconds),
    completedAttempts: Number(row.completed_attempts ?? 0), studentCount: Number(row.student_count ?? 0),
  }));
}

export async function createTeacherQuizAssignment(
  classId: string,
  quizId: string,
  dueAt: string,
  attemptLimit: number,
  timeLimitSeconds: number,
): Promise<string> {
  const { data, error } = await supabase.rpc('create_teacher_quiz_assignment', {
    p_class_id: classId,
    p_quiz_id: quizId,
    p_due_at: dueAt,
    p_attempt_limit: attemptLimit,
    p_time_limit_seconds: timeLimitSeconds,
  });
  throwIfError(error);
  if (!data) throw new Error('The assignment could not be created.');
  return String(data);
}

